<template>
  <div id="chartWrapper">
    <div class="columns">
      <div class="column is-half">
        <div id="chart"></div>
      </div>
    </div>

    <div id="itemModal" class="modal" :class="{ 'is-active' : isActive }">
      <div class="modal-background" @click="unsetItem"></div>
      <div class="modal-content">
        <div class="box">
          <h2>{{ item.name }}</h2>

          <p>{{ item.description }}</p>

          <hr/>

          <h3>Kennis / Documenten</h3>

          <div v-for="knowledgeItem in item.knowledge" class="box" @click="showKnowledge(knowledgeItem)">
            <article class="media">
              <div class="media-left">
                <figure class="image is-64x64">
                  <img v-if="hasImages(knowledgeItem.attachments)" :src="firstImage(knowledgeItem.attachments).file">
                </figure>
              </div>

              <div class="media-content">
                <div class="content">
                  <p>
                    <strong>{{ knowledgeItem.title }}</strong><br>

                    <template v-if="knowledgeItem.supplier">{{ knowledgeItem.supplier.name }}</template>
                  </p>
                </div>
              </div>
            </article>
          </div>

          <hr/>

          <h3>Producten</h3>

          <div v-for="product in item.products" class="box" @click="showProduct(product)">
            <article class="media">
              <div class="media-left">
                <figure class="image is-64x64">
                  <img v-if="product.images.length > 0" :src="product.images[0].file" :alt="product.name">
                </figure>
              </div>

              <div class="media-content">
                <div class="content">
                  <p>
                    <strong>{{ product.name }}</strong><br>

                    <template v-if="product.supplier">{{ product.supplier.name }}</template>
                  </p>
                </div>
              </div>
            </article>
          </div>
        </div>
      </div>

      <button class="modal-close is-large" aria-label="close" @click="unsetItem"></button>
    </div>

    <knowledge-modal :knowledge="knowledge" @close="hideKnowledge" @add="toggleKnowledgeInCollection(item.id, knowledge.id)"></knowledge-modal>
    <product-modal :product="product" @close="hideProduct" @add="toggleProductInCollection(item.id, product.id)"></product-modal>

  </div>
</template>

<script>
  import axios from 'axios'
  import * as d3 from 'd3'
  import { bus } from '@/main'
  import notebook from '@/vendor/circlepacking.js'
  import {Inspector, Runtime} from '@observablehq/notebook-runtime'
  import KnowledgeModal from '@/components/knowledge/KnowledgeModal'
  import ProductModal from '@/components/products/ProductModal'

  export default {
    components: {
      ProductModal,
      KnowledgeModal
    },
    data () {
      return {
        items: [],
        product: null,
        knowledge: null,
        products: [],
        padding: 0,
        textColor: [0,0,0],
        font: "BlinkMacSystemFont",
        links: [],
        circleColors: [
          "#bfbfbf",
          "#838383",
          "#4c4c4c",
          "#1c1c1c"
        ],
        socket: {
          calcX: null,
          calcY: null,
          scale: 1,
          root: null,
          context: null,
          hiddenContext: null,
          focus: null,
          interpolators: null,
          timeElapsed: 0,
          duration: 2000,
        },
        item: {},
        isActive: null,
        nextCol: 1,
        colToCircle: {}
      }
    },
    computed: {
      isAdmin () {
        return this.$store.getters.isAdmin
      },
      width () {
        return $("#dbb").innerWidth()
      },
      height () {
        return $(window).innerHeight()
      },
      centerX: {
        get () {
          return this.width / 2
        },
        set (value) {
          return value
        }
      },
      centerY: {
        get () {
          return this.height / 2
        },
        set (value) {
          return value
        }
      },
      diameter () {
        return Math.min(this.width * 0.9, this.height * 0.9)
      },
      radius () {
        return this.diameter / 2
      },
      itemsAsJson () {
        let str = JSON.stringify(this.items)
        return str.substring(1, str.length - 1)
      }
    },
    watch: {
      item (item) {
        Object.keys(item).length > 0 ? this.isActive = true : this.isActive = false;
      }
    },
    created () {
      this.fetchItems()
      this.fetchProducts()
    },
    mounted () {
      let chart = document.getElementById('chart')
      Runtime.load(notebook, Inspector.into(chart))

      let self = this

      setTimeout(function () {
        let circles = document.getElementsByTagName('circle')

        for (let circle of circles) {
          circle.addEventListener('click', (e) => {
            if (e.target.dataset.show === 'true') self.fetchItem(e.target.id)
          })
        }
      }, 2500)
    },
    methods: {
      getImages (attachments) {
        return attachments.filter((attachment) => {
          return this.isImage(attachment.file)
        })
      },
      hasImages (attachments) {
        return this.getImages(attachments).length > 0
      },
      isImage (filename) {
        let images = [
          'jpeg',
          'jpg',
          'png'
        ]

        return images.includes(filename.split('.').pop())
      },
      firstImage (attachments) {
        let images = this.getImages(attachments)

        return images[0]
      },
      buttonClass (itemId, productId) {
        if (this.$store.getters.inCollection(itemId, productId) === -1) {
          return 'is-white'
        } else {
          return 'is-danger'
        }
      },
      buttonCaption (itemId, productId) {
        if (this.$store.getters.inCollection(itemId, productId) === -1) {
          return 'Toevoegen aan collectie'
        } else {
          return 'Verwijderen uit collectie'
        }
      },
      showProduct (product) {
        this.product = product
      },
      hideProduct () {
        this.product = null
      },
      toggleProductInCollection (itemId, productId) {
        bus.$emit('toggleProductInCollection', {
          itemId: itemId,
          productId: productId,
        })
      },
      showKnowledge (knowledge) {
        this.knowledge = knowledge
      },
      hideKnowledge () {
        this.knowledge = null
      },
      toggleKnowledgeInCollection (itemId, knowledgeId) {
        bus.$emit('toggleKnowledgeInCollection', {
          itemId: itemId,
          knowledgeId: knowledgeId,
        })
      },
      unsetItem () {
        this.item = {}
      },
      fetchItem (id) {
        axios
          .get('/items/' + id)
          .then(response => {
            this.item = response.data
          })
          .catch(error => {
            console.error(error)
          });
      },
      fetchItems () {
        axios
          .get(`${process.env.ROOT_API}/api/data/items`)
          .then(response => {
            this.items = response.data
          })
          .catch(error => {
            console.error(error)
          });
      },
      fetchProducts () {
        axios
          .get('data/products')
          .then(response => {
            this.loading = false;
            this.products = response.data;
          }).catch(error => {
            this.loading = false;
            this.error = error.response.data.message || error.message;
          });
      },
      drawSvg (data) {
        let self = this

        let svg = d3.select("svg"),
          diameter = +svg.attr("width"),
          g = svg.append("g").attr("transform", "translate(2,2)"),
          format = d3.format(",d")

        let pack = d3.pack()
          .size([diameter - 4, diameter - 4])

        let root = d3.hierarchy(data)
          .sum(function (d) {
            return parseInt(d.size)
          })
          .sort(function (a, b) {
            return b.value - a.value
          })
          // console.log(root)
        var node = g.selectAll(".node")
          .data(pack(root).descendants())
          .enter().append("g")
            .attr("class", function (d) {
              return d.children ? "node" : "leaf node"
            })
            .attr("transform", function (d) {
              // console.log(d)
              return "translate(" + d.x + "," + d.y + ")"
            })

        node.append("title")
          .text(function (d) {
            return d.data.name + "\n" + format(d.value)
          })

        node.append("circle")
          .attr("r", function (d) {
            return d.r
          })

        node.filter(function (d) {
          return !d.children
        })
        .append("text")
        .attr("dy", "0.3em")
        .text(function (d) {
          return d.data.name.substring(0, d.r / 3)
        })

        node.on("click", function (d) {
          self.fetchItem(d.data.id)
        })
      }
    }
  }
</script>

<style scoped>
  h2 {
    font-size: 3em;
    font-weight: bold;
  }

  h3 {
    font-size: 2em;
    font-weight: bold;
  }

  li {
    line-height: 3em;
  }

  #itemModal .modal-background {
    background-color: rgba(10, 10, 10, 0.3);
  }

  #chart {
    padding: 2rem;
  }
</style>



// WEBPACK FOOTER //
// src/components/modules/Chart.vue